import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSectionHeadingSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';

const AboutUs: React.FC = () => {
  const { fontSize } = useFontSize();
  
  useEffect(() => {
    document.title = 'Stock Table - About Us';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />
      
      <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8 md:p-10">
          <h1 className={`${getPageTitleSizeClass(fontSize)} font-bold text-text-light-primary dark:text-text-primary mb-2`}>
            About Us
          </h1>

          <div className="prose prose-sm sm:prose-base max-w-none text-text-light-primary dark:text-text-primary mt-8">
            <section className="mb-8">
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
              Stock Table was born from the need for an accessible, user-friendly platform that makes stock research straightforward and effective.  
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                What We Support
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Currently, Stock Table supports only US common stocks and ETFs. We provide tables of common fundamental indicators, technical indicators, analyst views, and insider transactions to support your investment decisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                Our Philosophy
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
              The philosophy behind Stock Table is that in order to pick a stock and see the value of a stock, sometimes you would have to compare it to similar stocks. While charts are useful for analyzing a stock individually, tables make comparisons easier.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                Our Approach
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Stock Table provides a simple way to organize selected stocks into lists and view their statistics and indicators 
                in a tabular format.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Our table-based approach allows you to:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Compare multiple stocks side-by-side</li>
                <li>Quickly identify patterns and differences across securities</li>
                <li>Analyze stock-related data in a structured format</li>
                <li>Make informed investment decisions based on comprehensive comparisons</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                Investment Horizon
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Stock Table is not intended for day traders. To gain value from this tool, your investment horizon should be at 
                least days, weeks, months, or years. Our focus is on providing tools and data that support longer-term investment 
                research and decision-making.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                The data and analysis tools we provide are designed to help you understand a company's fundamentals, recent stock price movements, track its 
                performance over time, and make informed decisions about your investment portfolio.
              </p>
            </section>


            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                Feedback and Support
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If you encountered issues while using this service or have suggestions for making this service better, you're welcome to{' '}
                <Link
                  to="/contact-us"
                  className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors underline"
                >
                  contact us
                </Link>
                . Your input helps us improve Stock Table and make it better for everyone.
              </p>
            </section>

            <div className="mt-10 pt-6 border-t border-border-light dark:border-border-color">
              <div className="text-center">
                <Link
                  to="/"
                  className={`inline-block px-6 py-2 bg-primary dark:bg-highlight-gold text-gray-900 dark:text-gray-900 rounded-lg font-medium hover:opacity-90 transition-opacity ${getTextSizeClass(fontSize)}`}
                >
                  Return to Stock Table
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;

